import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { Button, Modal } from "@mapmycustomers/ui";

import AccessChangeOption from "@app/enum/AccessChangeOption";
import { isAccessChangePromptVisible } from "@app/store/accessChange";
import { chooseAccessChangeOption } from "@app/store/accessChange/actions";
import { RootState } from "@app/store/rootReducer";

import styles from "./AccessChangeModal.module.scss";

const messages = defineMessages({
  description: {
    id: "component.accessChangeModal.description",
    defaultMessage:
      "An administrator changed what you can do in Map My Customers after this page was opened. Updating the page now may clear what you've entered or stop the page from working.",
    description: "Access change modal: why the modal is shown",
  },
  keepPage: {
    id: "component.accessChangeModal.options.keepPage",
    defaultMessage: "Keep this page to copy my work",
    description:
      "Access change modal: button that leaves the page as it is so the user can copy input",
  },
  keepPageDescription: {
    id: "component.accessChangeModal.options.keepPage.description",
    defaultMessage:
      "<b>Keep this page to copy my work</b>: nothing on the page changes, so you can copy what you've entered somewhere safe. Saving may not work. Reload when you're done. Opening another page from the main menu reloads the page too.",
    description: "Access change modal: what the keep this page button does",
  },
  reload: {
    id: "component.accessChangeModal.options.reload",
    defaultMessage: "Reload page",
    description: "Access change modal: reload button",
  },
  reloadDescription: {
    id: "component.accessChangeModal.options.reload.description",
    defaultMessage:
      "<b>Reload page</b>: start fresh with your new access. Anything you haven't saved will be lost.",
    description: "Access change modal: what the reload button does",
  },
  title: {
    id: "component.accessChangeModal.title",
    defaultMessage: "Your access has changed",
    description: "Access change modal: title",
  },
  updatePage: {
    id: "component.accessChangeModal.options.updatePage",
    defaultMessage: "Update this page",
    description: "Access change modal: update this page button",
  },
  updatePageDescription: {
    id: "component.accessChangeModal.options.updatePage.description",
    defaultMessage:
      "<b>Update this page</b>: apply your new access without reloading. The page may stop working or clear what you've entered.",
    description: "Access change modal: what the update this page button does",
  },
});

const boldText = { b: (chunks: ReactNode[]) => <b>{chunks}</b> };

const AccessChangeModal = ({
  onChoose,
  open,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const handleReload = useCallback(() => onChoose(AccessChangeOption.RELOAD), [onChoose]);
  const handleUpdatePage = useCallback(() => onChoose(AccessChangeOption.UPDATE_PAGE), [onChoose]);
  const handleKeepPage = useCallback(() => onChoose(AccessChangeOption.KEEP_PAGE), [onChoose]);

  return (
    <Modal
      closable={false}
      footer={
        <div className={styles.footer}>
          <Button onClick={handleReload}>{intl.formatMessage(messages.reload)}</Button>
          <Button onClick={handleUpdatePage}>{intl.formatMessage(messages.updatePage)}</Button>
          <Button onClick={handleKeepPage}>{intl.formatMessage(messages.keepPage)}</Button>
        </div>
      }
      keyboard={false}
      maskClosable={false}
      open={open}
      size="small"
      title={intl.formatMessage(messages.title)}
      type="warning"
    >
      <p>{intl.formatMessage(messages.description)}</p>
      <ul className={styles.options}>
        <li>{intl.formatMessage(messages.reloadDescription, boldText)}</li>
        <li>{intl.formatMessage(messages.updatePageDescription, boldText)}</li>
        <li>{intl.formatMessage(messages.keepPageDescription, boldText)}</li>
      </ul>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  open: isAccessChangePromptVisible(state),
});

const mapDispatchToProps = {
  onChoose: chooseAccessChangeOption,
};

export default connect(mapStateToProps, mapDispatchToProps)(AccessChangeModal);
